import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate, useSearchParams } from "react-router-dom";
import GuestLayout from "@/components/guest/GuestLayout.jsx";
import { useSession } from "@/hooks/useSession.js";
import { guestSurveyApi } from "@/api/guest/guestSurveyApi";
import { CheckCircle2, Loader2, Send, Star } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const getApiData = (response) => {
  return response?.data?.data || response?.data?.Data || response?.data || null;
};

const getApiMessage = (error, fallback = "Something went wrong.") => {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.Message ||
    error?.response?.data?.title ||
    fallback
  );
};

const normalizeOption = (option, index) => {
  return {
    id: option.id || option.Id,
    label: option.label || option.Label || "",
    displayOrder: option.displayOrder ?? option.DisplayOrder ?? index + 1,
  };
};

const normalizeQuestion = (question, index) => {
  return {
    id: question.id || question.Id,
    label: question.label || question.Label || "",
    questionType: question.questionType || question.QuestionType || "ShortText",
    isRequired: question.isRequired ?? question.IsRequired ?? false,
    displayOrder: question.displayOrder ?? question.DisplayOrder ?? index + 1,
    placeholder:
      question.placeholder || question.Placeholder || "Type your answer...",
    minRating: question.minRating ?? question.MinRating ?? 1,
    maxRating: question.maxRating ?? question.MaxRating ?? 5,
    options: (question.options || question.Options || [])
      .map(normalizeOption)
      .sort((a, b) => a.displayOrder - b.displayOrder),
  };
};

const normalizeSurvey = (data) => {
  if (!data) return null;

  return {
    id: data.id || data.Id,
    title: data.title || data.Title || "",
    description: data.description || data.Description || "",
    status: data.status || data.Status || "Active",
    submitButtonText:
      data.submitButtonText || data.SubmitButtonText || "Submit Feedback",
    successMessage:
      data.successMessage ||
      data.SuccessMessage ||
      "Thank you for submitting your feedback.",
    questions: (data.questions || data.Questions || [])
      .map(normalizeQuestion)
      .sort((a, b) => a.displayOrder - b.displayOrder),
  };
};

const GuestSurveyPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const querySessionId =
    searchParams.get("sessionId") || searchParams.get("guestSessionId");
  const queryGuestName = searchParams.get("guest");
  const queryRoomName = searchParams.get("room");

  const { session, hotel, room, loading: sessionLoading } = useSession();

  const [survey, setSurvey] = useState(null);
  const [answers, setAnswers] = useState({});
  const [guestNameInput, setGuestNameInput] = useState(queryGuestName || "");
  const [guestInfo, setGuestInfo] = useState({
    guestEmail: "",
    guestPhone: "",
  });

  const [loadingSurvey, setLoadingSurvey] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loadError, setLoadError] = useState("");

  const loading = sessionLoading || loadingSurvey;

  useEffect(() => {
    if (session) {
      setGuestInfo((prev) => ({
        guestEmail:
          session?.guestEmail ||
          session?.GuestEmail ||
          session?.email ||
          session?.Email ||
          prev.guestEmail ||
          "",
        guestPhone:
          session?.guestPhone ||
          session?.GuestPhone ||
          session?.phone ||
          session?.Phone ||
          prev.guestPhone ||
          "",
      }));
      if (!guestNameInput) {
        setGuestNameInput(
          session?.guestName || session?.GuestName || session?.name || ""
        );
      }
    }
  }, [session]);

  const loadSurvey = async (overrideSessionId) => {
    try {
      setLoadingSurvey(true);
      setLoadError("");

      const params = overrideSessionId ? { sessionId: overrideSessionId } : undefined;
      const response = await guestSurveyApi.getActiveGuestSurvey(params);
      const data = getApiData(response);

      setSurvey(normalizeSurvey(data));
    } catch (error) {
      const status = error?.response?.status;

      if (!overrideSessionId && (status === 401 || status === 403)) {
        navigate("/guest/expired", { replace: true });
        return;
      }

      if (status === 404) {
        setSurvey(null);
        return;
      }

      const message = getApiMessage(error, "Failed to load feedback form.");
      setLoadError(message);
      toast.error(message);
      setSurvey(null);
    } finally {
      setLoadingSurvey(false);
    }
  };

  useEffect(() => {
    if (!sessionLoading && !session && !querySessionId) {
      navigate("/guest/expired", { replace: true });
      return;
    }

    if (!sessionLoading && (session || querySessionId)) {
      loadSurvey(querySessionId);
    }
  }, [sessionLoading, session, querySessionId, navigate]);

  const guestName =
    session?.guestName ||
    session?.GuestName ||
    "Guest";

  const requiredQuestions = useMemo(() => {
    return survey?.questions?.filter((question) => question.isRequired) || [];
  }, [survey]);

  const answeredCount = useMemo(() => {
    if (!survey?.questions?.length) return 0;

    return survey.questions.filter((question) => {
      const value = answers[question.id];

      if (value === undefined || value === null) return false;
      if (typeof value === "string" && !value.trim()) return false;

      return true;
    }).length;
  }, [survey, answers]);

  const progressPercent = useMemo(() => {
    if (!survey?.questions?.length) return 0;
    return Math.round((answeredCount / survey.questions.length) * 100);
  }, [answeredCount, survey]);

  const updateAnswer = (questionId, value) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: value,
    }));
  };

  const updateGuestInfo = (field, value) => {
    setGuestInfo((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const validateAnswers = () => {
    const missingQuestion = requiredQuestions.find((question) => {
      const value = answers[question.id];

      if (value === undefined || value === null) return true;
      if (typeof value === "string" && !value.trim()) return true;

      return false;
    });

    if (missingQuestion) {
      toast.error(`Please answer: ${missingQuestion.label}`);
      return false;
    }

    return true;
  };

  const buildAnswerPayload = (question) => {
    const value = answers[question.id];

    if (question.questionType === "Rating") {
      return {
        questionId: question.id,
        answerText: value ? String(value) : "",
        answerNumber: value ? Number(value) : null,
        selectedOptionId: null,
      };
    }

    if (question.questionType === "SingleChoice") {
      const selectedOption = question.options.find(
        (option) => option.id === value,
      );

      return {
        questionId: question.id,
        answerText: selectedOption?.label || "",
        answerNumber: null,
        selectedOptionId: value || null,
      };
    }

    if (question.questionType === "YesNo") {
      return {
        questionId: question.id,
        answerText: value || "",
        answerNumber: null,
        selectedOptionId: null,
      };
    }

    return {
      questionId: question.id,
      answerText: value || "",
      answerNumber: null,
      selectedOptionId: null,
    };
  };

  const handleSubmit = async () => {
    if (!validateAnswers()) return;

    try {
      setSubmitting(true);

      const resolvedGuestName =
        guestNameInput.trim() ||
        session?.guestName ||
        session?.GuestName ||
        queryGuestName ||
        "Guest";

      const payload = {
        guestName: resolvedGuestName,
        guestEmail: guestInfo.guestEmail.trim(),
        guestPhone: guestInfo.guestPhone.trim(),
        answers: survey.questions.map(buildAnswerPayload),
      };

      const params = querySessionId ? { sessionId: querySessionId } : undefined;
      await guestSurveyApi.submitSurvey(survey.id, payload, params);

      toast.success(survey.successMessage || "Thank you for your feedback");
      setSubmitted(true);
    } catch (error) {
      toast.error(getApiMessage(error, "Failed to submit feedback."));
    } finally {
      setSubmitting(false);
    }
  };

  const renderQuestionInput = (question) => {
    const value = answers[question.id] || "";

    if (question.questionType === "LongText") {
      return (
        <textarea
          value={value}
          onChange={(e) => updateAnswer(question.id, e.target.value)}
          placeholder={question.placeholder || "Type your answer..."}
          rows={5}
          className="w-full resize-none rounded-2xl border border-white/40 bg-white/80 px-4 py-3 text-base text-slate-800 outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20 sm:text-sm"
        />
      );
    }

    if (question.questionType === "Rating") {
      const min = Number(question.minRating || 1);
      const max = Number(question.maxRating || 5);
      const ratings = Array.from(
        { length: max - min + 1 },
        (_, index) => min + index,
      );

      return (
        <div className="grid grid-cols-5 gap-2 sm:flex sm:flex-wrap">
          {ratings.map((rating) => {
            const isActive = Number(value) === rating;

            return (
              <Button
                key={rating}
                type="button"
                onClick={() => updateAnswer(question.id, rating)}
                className={[
                  "inline-flex h-12 items-center justify-center gap-1 rounded-2xl border text-sm font-semibold transition sm:w-auto sm:px-4",
                  isActive
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-white/40 bg-white/80 text-slate-700 hover:bg-white",
                ].join(" ")}
              >
                <Star
                  className={["h-4 w-4", isActive ? "fill-current" : ""].join(
                    " ",
                  )}
                />
                <span>{rating}</span>
              </Button>
            );
          })}
        </div>
      );
    }

    if (question.questionType === "SingleChoice") {
      return (
        <div className="grid gap-2">
          {question.options.map((option) => {
            const isActive = value === option.id;

            return (
              <Button
                key={option.id}
                type="button"
                onClick={() => updateAnswer(question.id, option.id)}
                className={[
                  "flex min-h-12 w-full items-center justify-between rounded-2xl border px-4 py-3 text-left text-base transition sm:text-sm",
                  isActive
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-white/40 bg-white/80 text-slate-700 hover:bg-white",
                ].join(" ")}
              >
                <span className="min-w-0 break-words">{option.label}</span>

                {isActive && <CheckCircle2 className="ml-3 h-5 w-5 shrink-0" />}
              </Button>
            );
          })}
        </div>
      );
    }

    if (question.questionType === "YesNo") {
      return (
        <div className="grid grid-cols-2 gap-2">
          {["Yes", "No"].map((option) => {
            const isActive = value === option;

            return (
              <Button
                key={option}
                type="button"
                onClick={() => updateAnswer(question.id, option)}
                className={[
                  "h-12 rounded-2xl border px-4 text-base font-semibold transition sm:text-sm",
                  isActive
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-white/40 bg-white/80 text-slate-700 hover:bg-white",
                ].join(" ")}
              >
                {option}
              </Button>
            );
          })}
        </div>
      );
    }

    return (
      <Input
        value={value}
        onChange={(e) => updateAnswer(question.id, e.target.value)}
        placeholder={question.placeholder || "Type your answer..."}
        className="h-12 w-full rounded-2xl border border-white/40 bg-white/80 px-4 text-base text-slate-800 outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20 sm:text-sm"
      />
    );
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  const effectiveSession =
    session ||
    (querySessionId
      ? {
          sessionId: querySessionId,
          guestName: guestNameInput || queryGuestName || "Valued Guest",
          roomName: queryRoomName || "",
        }
      : null);
  const effectiveRoom =
    room ||
    (queryRoomName
      ? { roomNumber: queryRoomName, name: queryRoomName }
      : null);

  if (!survey) {
    return (
      <GuestLayout hotel={hotel} room={effectiveRoom} session={effectiveSession}>
        <div className="mx-auto max-w-2xl px-2">
          <div className="rounded-3xl border border-white/40 bg-white/85 p-6 text-center shadow-sm backdrop-blur-md sm:p-8">
            <h1 className="text-2xl font-bold text-slate-800">
              {loadError ? "Unable to load feedback" : "No active feedback form available"}
            </h1>
            <p className="mt-2 text-sm text-slate-600 sm:text-base">
              {loadError ||
                "There is no active feedback form available right now."}
            </p>
          </div>
        </div>
      </GuestLayout>
    );
  }

  if (submitted) {
    return (
      <GuestLayout hotel={hotel} room={effectiveRoom} session={effectiveSession}>
        <div className="mx-auto max-w-2xl px-2">
          <div className="rounded-3xl border border-white/40 bg-white/85 p-6 text-center shadow-sm backdrop-blur-md sm:p-8">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
              <CheckCircle2 className="h-8 w-8" />
            </div>

            <h1 className="mt-4 text-2xl font-bold text-slate-800">
              Thank you!
            </h1>

            <p className="mt-2 text-sm text-slate-600 sm:text-base">
              {survey.successMessage ||
                "Your feedback has been submitted successfully."}
            </p>

            <Button
              type="button"
              onClick={() => navigate(session ? "/guest/home" : "/")}
              className="mt-6 h-12 w-full rounded-2xl bg-primary px-5 text-sm font-medium text-primary-foreground transition hover:bg-primary/90 sm:w-auto"
            >
              Back to home
            </Button>
          </div>
        </div>
      </GuestLayout>
    );
  }

  return (
    <>
      <Helmet>
        <title>{`Feedback - ${hotel?.name || "Hotel"}`}</title>
        <meta name="description" content="Submit hotel guest feedback" />
      </Helmet>

      <GuestLayout hotel={hotel} room={effectiveRoom} session={effectiveSession}>
        <div className="mx-auto max-w-3xl space-y-5 px-1 pb-24 sm:space-y-6 sm:px-0 sm:pb-0">
          <div className="rounded-3xl bg-primary p-5 text-primary-foreground shadow-sm sm:p-6">
            <p className="text-sm font-medium opacity-80">Guest Feedback</p>

            <h1 className="mt-1 text-2xl font-bold leading-tight text-balance sm:text-3xl">
              {survey.title}
            </h1>

            {survey.description && (
              <p className="mt-3 text-sm leading-relaxed opacity-90 sm:text-base">
                {survey.description}
              </p>
            )}

            <div className="mt-5">
              <div className="mb-2 flex items-center justify-between text-xs font-medium opacity-90">
                <span>
                  {answeredCount} of {survey.questions.length} answered
                </span>
                <span>{progressPercent}%</span>
              </div>

              <div className="h-2 overflow-hidden rounded-full bg-white/25">
                <div
                  className="h-full rounded-full bg-white transition-all"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>
          </div>

          <div className="rounded-3xl border border-white/40 bg-white/85 p-4 shadow-sm backdrop-blur-md sm:p-5">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Guest Details
            </p>

            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              <Input
                value={guestNameInput}
                onChange={(e) => setGuestNameInput(e.target.value)}
                placeholder="Guest name"
                className="h-12 rounded-2xl border border-white/40 bg-white/80 px-4 text-sm font-semibold text-slate-800 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
              />

              <Input
                value={guestInfo.guestEmail}
                onChange={(e) => updateGuestInfo("guestEmail", e.target.value)}
                placeholder="Email (optional)"
                className="h-12 rounded-2xl border border-white/40 bg-white/80 px-4 text-sm text-slate-800 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
              />

              <Input
                value={guestInfo.guestPhone}
                onChange={(e) => updateGuestInfo("guestPhone", e.target.value)}
                placeholder="Phone (optional)"
                className="h-12 rounded-2xl border border-white/40 bg-white/80 px-4 text-sm text-slate-800 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
              />
            </div>
          </div>

          <div className="space-y-4">
            {survey.questions.map((question, index) => (
              <div
                key={question.id}
                className="rounded-3xl border border-white/40 bg-white/85 p-4 shadow-sm backdrop-blur-md sm:p-5"
              >
                <div className="mb-4">
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    Question {index + 1}
                  </p>

                  <h2 className="mt-1 text-base font-semibold leading-snug text-slate-800 sm:text-lg">
                    {question.label}
                    {question.isRequired && (
                      <span className="ml-1 text-red-500">*</span>
                    )}
                  </h2>
                </div>

                {renderQuestionInput(question)}
              </div>
            ))}
          </div>

          <div className="fixed bottom-0 left-0 right-0 z-20 border-t bg-background/95 p-3 shadow-[0_-8px_20px_rgba(15,23,42,0.08)] backdrop-blur sm:static sm:border-0 sm:bg-transparent sm:p-0 sm:shadow-none">
            <div className="mx-auto max-w-3xl">
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={submitting}
                className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-primary px-5 text-sm font-medium text-primary-foreground transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
              >
                {submitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                {survey.submitButtonText || "Submit Feedback"}
              </Button>
            </div>
          </div>
        </div>
      </GuestLayout>
    </>
  );
};

export default GuestSurveyPage;
